<script setup lang="ts">
import jobFormDetails from '@/components/support-seeker/jobFormDetails.vue';
import { AppButton, AppFormCard, AppFormHeader } from '@/core/components';
import icPersonalDetails from '@/assets/img/icon/support-seeker/ic-personal-details.svg';
import JobFormSupportRequirement from './JobFormSupportRequirement.vue';
import JobFormDocAndQualificationsRequirement from './JobFormDocAndQualificationsRequirement.vue';
import SupportPreference from './SupportPreference.vue';
import JobListingService from '@/services/JobListingService';
import { useFilterUser } from '@/store/filterUser';
import { storeToRefs } from 'pinia';
import type { ValidationArgs } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import addressService from '@/services/AddressService';

const route = useRoute();
const jobId = computed(() => route.query.jobId as string);
const isDraftMode = computed(() => route.query.isDraft === 'true');

// Add reactive variables for addresses
const useDefaultAddressItems = ref([]);
const useDefaultAddressPending = ref(false);

const getJobDetails = async () => {
  if (!jobId.value) {
    console.log('No jobId available, skipping API call');
    return;
  }

  try {
    const { data } = await useFetchData<any>('/jobs/' + jobId.value, {
      method: 'GET',
    });

    if (data.value) {
      jobDetails.value = data.value;
      // Re-initialize form with fetched data
      form.value = initializeForm(data.value);
      isEdit.value = true;

      // Reset validation state after loading data in draft mode
      if (isDraftMode.value) {
        await nextTick(() => {
          v$.value.$reset();
        });
      }
    }
  } catch (error) {
    console.error('Error fetching job details:', error);
  }
};

const props = withDefaults(
  defineProps<{
    jobTitle?: string;
    description?: string;
    payRate?: string;
    toBeAdvised?: boolean;
    isOnboarding?: boolean;
    isCreateJob?: boolean;
    saveButtonLabel?: string;
    distance_from_me?: string;
  }>(),
  {
    jobTitle: 'Create a Job to find a Support Worker',
    saveButtonLabel: 'Publish',
    description: '',
    payRate: '',
    toBeAdvised: false,
    isOnboarding: true,
    isCreateJob: false,
    distance_from_me: '',
  }
);

const { options } = storeToRefs(useFilterUser());

// Define the job details type
interface JobDetails {
  title?: string;
  description?: string;
  address?: string;
  pay_rate?: { min?: number; max?: number };
  min_payrate?: string;
  max_payrate?: string;
  support_requirements?: any[];
  qualifications?: any[];
  support_worker_gender?: string;
  is_ongoing_job?: boolean;
  days_time?: any;
  support_worker_age_range?: string;
  userDetails?: any;
  is_tba?: boolean;
  is_published?: boolean;
  is_use_default_address?: boolean;
  distance_from_me?: string;
}

const jobDetails = ref<JobDetails>({});
const isEdit = ref(false);

// Initialize form with default values
const initializeForm = (jobData?: JobDetails) => {
  return {
    // jobTitle: jobData?.title || '',
    description: jobData?.description || '',
    address: jobData?.address || '',
    distance_from_me: jobData?.distance_from_me || '',
    useDefaultAddress: jobData?.is_use_default_address || '',
    payRateMin: jobData?.min_payrate || '',
    payRateMax: jobData?.max_payrate || '',
    toBeAdvised: jobData?.is_tba || false,
    services_offered: jobData?.support_requirements || options.value.services_offered || [],
    qualifications_needed: jobData?.qualifications || (props.isCreateJob ? [] : options.value.qualifications_needed || []),
    userDetails: {
      gender: jobData?.support_worker_gender || '',
      is_ongoing_job: jobData?.is_ongoing_job === true ? 'yes' : 'no',
      days_time: jobData?.days_time || '',
      support_worker_age_range: jobData?.support_worker_age_range || '',
    },
  };
};

// Initialize form with empty values first
const form = ref(initializeForm());

async function loadAddresses() {
  useDefaultAddressPending.value = true;

  try {
    const { data } = await addressService.getAddresses();
    useDefaultAddressItems.value = data.value || [];
  } catch (err) {
    useDefaultAddressItems.value = [];
    showSnackbar({
      text: 'Failed to load addresses. Please try again.',
      state: 'error'
    });
  } finally {
    useDefaultAddressPending.value = false;
  }
}

onMounted(async () => {
  await nextTick(async () => {
    if (jobId.value) {
      getJobDetails();
    }
    loadAddresses();
    // Reset validation state when in draft mode to prevent immediate validation
    if (isDraftMode.value) {
      v$.value.$reset();
    }
  })
});

const { VALIDATIONS } = useValidation();

const rules = computed<ValidationArgs<Partial<any>>>(() => {

  // Create base rules
  const baseRules = {
    // jobTitle: {
    //   required: VALIDATIONS.required('Job Title'),
    // },
    description: {
      required: VALIDATIONS.required('Description'),
    },
    distance_from_me: {
      required: VALIDATIONS.required('Distance From Me'),
    },
    services_offered: {
      required: VALIDATIONS.required('Services Offered'),
      minLength: VALIDATIONS.minLength('Services Offered', 1),
    },
    address: {
      required: () => {
        return !!form.value.toBeAdvised; // return boolean for validation
      },
    },
    useDefaultAddress: {
      required: () => {
        return !!form.value.toBeAdvised; // return boolean for validation
      },
    },
    userDetails: {
      gender: {
        required: VALIDATIONS.required('Gender'),
      },
      is_ongoing_job: {
        required: VALIDATIONS.required('Job Type'),
      },
      days_time: {
        required: VALIDATIONS.required('Days & Time'),
      },
      support_worker_age_range: {
        required: VALIDATIONS.required('Age Range'),
      },
    },
  };

  // Add conditional validation for address fields
  if (form.value.useDefaultAddress && form.value.useDefaultAddress !== '') {
    // If default address is selected, address field is not required
    baseRules.address = {
      required: false,
    };
  } else if (form.value.address && form.value.address !== '') {
    // If address is entered, default address field is not required
    baseRules.useDefaultAddress = {
      required: false,
    };
  } else {
    // If neither is selected, both are required
    baseRules.address = {
      required: VALIDATIONS.required('Address'),
    };
    baseRules.useDefaultAddress = {
      required: VALIDATIONS.required('Use Default Address'),
    };
  }
  if (!form.value.toBeAdvised) {
    baseRules.payRateMin = {
      required: VALIDATIONS.required('Pay Rate Minimum'),
    };
    baseRules.payRateMax = {
      required: VALIDATIONS.required('Pay Rate Maximum'),
    };
  }

  return baseRules;
});



const { v$, handleSubmit, getErrors,  } = useForm({
  form: form,
  rules: rules,
});

// Simple watcher to refresh validation when conditional fields change
watch([() => form.value.toBeAdvised], () => {
  nextTick(() => {
    if (v$.value) {
      v$.value.$validate();
    }
  });
});


const isDisabled = computed((): boolean => {
  // For draft mode, only disable if loading, not for validation errors
  if (isDraftMode.value) {
    return loading.value;
  }
  // For regular mode, disable for validation errors or loading
  return v$.value.$invalid || loading.value;
});

const errors = computed(() => {
  return getErrors(serverError.value || {});
});

const { showSnackbar } = useSnackbar();
const { data, error: serverError, execute: execCreateJob, pending: loading } = useSubmit();

const cancel = () => {
  if (isDraftMode.value) {
    navigateTo({ path: '/requests', query: { tab: 'myDrafts' } });
  } else {
    navigateTo({ path: '/requests', query: { tab: 'myListing' } });
  }
};

const submitForm = async () => {
  handleSubmit(async function () {
    try {
      const jobData = {
        // title: form.value.jobTitle,
        description: form.value.description,
        is_use_default_address: form.value.useDefaultAddress ? true : false,
        address: form.value.address.full_name ? form.value.address.full_name : form.value.address,
        distance_from_me: form.value.distance_from_me,
        job_location: form.value.useDefaultAddress,
        payRateMin: form.value.payRateMin,
        payRateMax: form.value.payRateMax,
        support_requirements: form.value.services_offered.map((service: any) => ({ id: service.id })),
        required_docs_and_qualification: form.value.qualifications_needed.map((qualification: any) => ({
          value: qualification.name,
          type: qualification.slug
        })),
        support_worker_gender: form.value.userDetails.gender,
        is_ongoing_job: form.value.userDetails.is_ongoing_job === 'yes' ? true : false,
        days_time: form.value.userDetails.is_ongoing_job === 'yes' ?
          form.value.userDetails.days_time :
          {
            start_date: typeof form.value.userDetails.days_time === 'object' ? form.value.userDetails.days_time.start_date : '',
            end_date: typeof form.value.userDetails.days_time === 'object' ? form.value.userDetails.days_time.end_date : '',
          },
        support_worker_age_range: form.value.userDetails.support_worker_age_range,
        is_tba: form.value.toBeAdvised || false,
      };

      if (isEdit.value) {
        await execCreateJob('jobs/' + jobId.value, {
          method: 'PUT',
          body: jobData,
        });
      } else {
        await execCreateJob('jobs', {
          method: 'POST',
          body: jobData,
        });
      }

      navigateTo({ path: '/requests', query: { tab: 'myListing' } });

      if (!serverError.value) {
        showSnackbar({
          text: isDraftMode.value ? 'Draft updated successfully!' : 'Job created successfully!',
          state: 'success',
        });

        // Reset form after successful submission
        form.value = initializeForm();
        v$.value.$reset();
      } else {
        showSnackbar({
          text: 'Failed to create job. Please try again.',
          state: 'error',
        });
      }
    } catch (error) {
      console.error('Error creating job:', error);
      showSnackbar({
        text: 'An error occurred while creating the job. Please try again.',
        state: 'error',
      });
    }
  });
};

const deleteJob = async () => {
  if (jobId.value) {
    await execCreateJob('jobs/' + jobId.value, {
      method: 'DELETE',
    });
    navigateTo({ path: '/requests', query: { tab: 'myListing' } });
    showSnackbar({
      text: 'Job deleted successfully!',
      state: 'success',
    });
  }

};

const publishJob = async () => {
  // v$.value.$validate();
  // if (v$.value.$invalid) {
  //   return;
  // }
  try {
    const jobData = {
      // title: form.value.jobTitle,
      description: form.value.description,
      is_use_default_address: form.value.useDefaultAddress ? true : false,
      address: form.value.address.full_name ? form.value.address.full_name : form.value.address,
      distance_from_me: form.value.distance_from_me,
      job_location: form.value.useDefaultAddress,
      payRateMin: form.value.payRateMin,
      payRateMax: form.value.payRateMax,
      support_requirements: form.value.services_offered.map((service: any) => ({ id: service.id })),
      required_docs_and_qualification: form.value.qualifications_needed.map((qualification: any) => ({
        value: qualification.name,
        type: qualification.slug
      })),
      support_worker_gender: form.value.userDetails.gender,
      is_ongoing_job: form.value.userDetails.is_ongoing_job === 'yes' ? true : false,
      days_time: form.value.userDetails.is_ongoing_job === 'yes' ?
        form.value.userDetails.days_time :
        {
          start_date: typeof form.value.userDetails.days_time === 'object' ? form.value.userDetails.days_time.start_date : '',
          end_date: typeof form.value.userDetails.days_time === 'object' ? form.value.userDetails.days_time.end_date : '',
        },
      support_worker_age_range: form.value.userDetails.support_worker_age_range,
      is_tba: form.value.toBeAdvised || false,
    };
    if(jobId.value){
      await execCreateJob('jobs/' + jobId.value, {
        method: 'PUT',
        body: jobData,
      });
      showSnackbar({
        text: 'Job published successfully!',
        state: 'success',
      });
      navigateTo({ path: '/requests', query: { tab: 'myListing' } });
    }
  } catch (error) {
    console.error('Error publishing job:', error);
    showSnackbar({
      text: 'An error occurred while publishing the job. Please try again.',
      state: 'error',
    });
  }
};

const draftChanges = async () => {
  try {
    const jobData = {
      // title: form.value.jobTitle,
      description: form.value.description || '',
      address: form.value.address.full_name ? form.value.address.full_name : form.value.address ? form.value.address : '',
      distance_from_me: form.value.distance_from_me,
      job_location: form.value.useDefaultAddress || '',
      is_use_default_address: form.value.useDefaultAddress ? true : false,
      payRateMin: form.value.payRateMin || '',
      payRateMax: form.value.payRateMax || '',
      support_requirements: form.value.services_offered?.map((service: any) => ({ id: service.id })) || [],
      required_docs_and_qualification: form.value.qualifications_needed?.map((qualification: any) => ({
        value: qualification.name,
        type: qualification.slug
      })) || [],
      support_worker_gender: form.value.userDetails?.gender || '',
      is_ongoing_job: form.value.userDetails?.is_ongoing_job === 'yes' ? true : false,
      days_time: form.value.userDetails?.is_ongoing_job === 'yes' ?
        form.value.userDetails.days_time || '' :
        {
          start_date: typeof form.value.userDetails?.days_time === 'object' ? form.value.userDetails.days_time.start_date : '',
          end_date: typeof form.value.userDetails?.days_time === 'object' ? form.value.userDetails.days_time.end_date : '',
        },
      support_worker_age_range: form.value.userDetails?.support_worker_age_range || '',
      is_tba: form.value.toBeAdvised || false,
      is_draft: true,
      ...(isDraftMode.value && { is_draft: true }),
    };

    // If editing a draft, always save as draft regardless of isEdit status
    if (isEdit.value && isDraftMode.value) {
      await execCreateJob('jobs/' + jobId.value, {
        method: 'PUT',
        body: jobData,
      });
    } else if (isEdit.value) {
      await execCreateJob('jobs/' + jobId.value, {
        method: 'PUT',
        body: jobData,
      });
    } else {
      await execCreateJob('jobs', {
        method: 'POST',
        body: jobData,
      });
    }

    if (!serverError.value) {
      showSnackbar({
        text: 'Draft saved successfully!',
        state: 'success',
      });

      if (isEdit.value && isDraftMode.value) {
        navigateTo({ path: '/requests', query: { tab: 'myDrafts' } });
      } else {
        navigateTo({ path: '/requests', query: { tab: 'myListing' } });
      }

    } else {
      showSnackbar({
        text: 'Failed to save draft. Please try again.',
        state: 'error',
      });
    }
  } catch (error) {
    console.error('Error saving draft:', error);
    showSnackbar({
      text: 'An error occurred while saving the draft. Please try again.',
      state: 'error',
    });
  }
};

</script>

<template>
  <v-form @submit.prevent="submitForm" class="add-job-form pa-0">
    <AppFormCard :icon="true" :title="jobTitle" class="mb-6">
      <JobFormSupportRequirement class="mb-6" v-model:services_offered="form.services_offered"
        :errors="{ services: v$.services_offered.$errors }">
        <!-- You can add or pass SelectServices or other logic here if needed -->
      </JobFormSupportRequirement>
      <template #icon>
        <v-icon><img :src="icPersonalDetails" width="20px" height="20px" alt="Personal details icon" /></v-icon>
      </template>
    </AppFormCard>

    <AppFormCard class="mb-6">
      <jobFormDetails v-model:description="form.description" v-model:address="form.address"
        v-model:payRateMin="form.payRateMin" v-model:payRateMax="form.payRateMax"
        v-model:useDefaultAddress="form.useDefaultAddress" v-model:distance_from_me="form.distance_from_me"
        :useDefaultAddressItems="useDefaultAddressItems" :toBeAdvised="form.toBeAdvised"
        @update:toBeAdvised="(value) => { form.toBeAdvised = value; }" :is-disabled="isDisabled" :errors="errors"
        :isDraft="isDraftMode" />
    </AppFormCard>

    <AppFormCard class="mb-6">
      <JobFormDocAndQualificationsRequirement class="mb-6" v-model:qualifications_needed="form.qualifications_needed">
        <!-- You can add or pass QualificationsNeeded or other logic here if needed -->
      </JobFormDocAndQualificationsRequirement>
    </AppFormCard>

    <SupportPreference class="mb-6" v-model:userDetails="form.userDetails" :jobDetails="jobDetails">
      You can add or pass QualificationsNeeded or other logic here if needed
    </SupportPreference>
    <AppFormHeader :jobTitle="jobTitle" :description="description" class="mb-6" @back="cancel()" @draft="draftChanges"
      @delete="deleteJob" @submit="publishJob" :isDraft="isDraftMode" :isEdit="isEdit" :showCancelButton="true"
      :is-onboarding="isOnboarding" :showDeleteButton="true" :isPublished="jobDetails.is_published"
      :isCreateJob="isCreateJob" :saveButtonLabel="saveButtonLabel" />
    <AppButton v-if="isOnboarding" class="mt-6" color="primary" block :disabled="isDisabled" :loading="loading">Continue
    </AppButton>
  </v-form>
</template>
<style lang="scss" scoped>
.add-job-form {
  width: 100%;
  max-width: var(--form-max-width);
}
</style>
